import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, MessageSquare, TrendingUp, Bell, Send, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface WelcomeOverlayProps {
  userName: string;
  onComplete: () => void;
}

const greetings = [
  "Welcome back",
  "Great to see you",
  "How's your day",
  "Ready to create",
  "Let's get started"
];

const updates = [
  {
    icon: TrendingUp,
    text: "Your latest post received 150% more engagement!",
    color: "text-green-400"
  },
  {
    icon: Bell,
    text: "3 scheduled posts are ready to go live today",
    color: "text-blue-400"
  },
  {
    icon: MessageSquare,
    text: "5 new comments on your recent posts",
    color: "text-purple-400"
  }
];

const letterAnimation = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  exit: { 
    opacity: 0,
    y: -20,
    transition: { duration: 1.5, ease: [0.4, 0, 0.2, 1] }
  }
};

const WelcomeOverlay: React.FC<WelcomeOverlayProps> = ({ userName, onComplete }) => {
  const navigate = useNavigate();
  const [greeting] = useState(() => greetings[Math.floor(Math.random() * greetings.length)]);
  const [message, setMessage] = useState('');
  const [showUpdates, setShowUpdates] = useState(false);
  const [buttonHighlight, setButtonHighlight] = useState(false);
  const letters = [...greeting];
  const nameLetters = [...userName];

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowUpdates(true);
    }, (letters.length + nameLetters.length) * 50 + 500);

    return () => clearTimeout(timer);
  }, [letters.length, nameLetters.length]);
  
  // Add keyboard event listener for Enter key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (message.trim()) {
          handleSendMessage();
        } else {
          onComplete();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Highlight button animation
    const interval = setInterval(() => {
      setButtonHighlight(prev => !prev);
    }, 2000);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearInterval(interval);
    };
  }, [message, onComplete]);

  const handleSendMessage = () => {
    if (message.trim()) {
      // Store the message in localStorage to be picked up by the Creator Assistant
      localStorage.setItem('welcome_message', message.trim());
      // Navigate to the Creator Assistant page
      navigate('/creator-assistant');
      // Close the welcome overlay
      onComplete();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ 
        opacity: 0,
        transition: { 
          duration: 1.5,
          ease: [0.4, 0, 0.2, 1],
          when: "afterChildren"
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-indigo-600/95 to-purple-600/95 backdrop-blur-sm"
    >
      <div className="w-full max-w-2xl text-center text-white p-8">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ 
            scale: 1, 
            rotate: 0,
            transition: {
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 1
            }
          }}
          exit={{ 
            scale: 0,
            rotate: 180,
            transition: { 
              duration: 1.5,
              ease: [0.4, 0, 0.2, 1]
            }
          }}
          className="mb-8 inline-block"
        >
          <Cloud className="h-16 w-16" />
        </motion.div>

        <div className="overflow-hidden mb-12">
          <div className="flex justify-center mb-4">
            {letters.map((letter, index) => (
              <motion.span
                key={index}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={letterAnimation}
                transition={{
                  duration: 0.5,
                  delay: index * 0.05,
                  type: "spring",
                  damping: 12,
                  stiffness: 200
                }}
                className="text-4xl font-bold inline-block"
                style={{ display: letter === ' ' ? 'inline-block' : 'inline-block', width: letter === ' ' ? '0.5em' : 'auto' }}
              >
                {letter}
              </motion.span>
            ))}
          </div>

          <div className="flex justify-center">
            {nameLetters.map((letter, index) => (
              <motion.span
                key={index}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={letterAnimation}
                transition={{
                  duration: 0.5,
                  delay: (letters.length * 0.05) + (index * 0.05),
                  type: "spring",
                  damping: 12,
                  stiffness: 200
                }}
                className="text-5xl font-bold text-indigo-200 inline-block"
                style={{ display: letter === ' ' ? 'inline-block' : 'inline-block', width: letter === ' ' ? '0.5em' : 'auto' }}
              >
                {letter}
              </motion.span>
            ))}
            <motion.span
              initial="initial"
              animate="animate"
              exit="exit"
              variants={letterAnimation}
              transition={{
                duration: 0.5,
                delay: (letters.length * 0.05) + (nameLetters.length * 0.05),
                type: "spring",
                damping: 12,
                stiffness: 200
              }}
              className="text-5xl font-bold text-indigo-200"
            >
              !
            </motion.span>
          </div>
        </div>

        <AnimatePresence>
          {showUpdates && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4 mb-8"
            >
              {updates.map((update, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-center space-x-3"
                >
                  <update.icon className={`h-5 w-5 ${update.color}`} />
                  <span className="text-white/90">{update.text}</span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="max-w-md mx-auto relative"
        >
          <div className="relative">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Any questions for me?"
              className="w-full px-4 py-3 bg-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Ask a question"
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-white/10 rounded-full transition-colors"
              onClick={handleSendMessage}
              aria-label="Send message"
            >
              <Send className="h-5 w-5 text-white/70" />
            </button>
          </div>
        </motion.div>

        <motion.div className="mt-6 flex flex-col items-center">
          <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onComplete}
          className={`px-6 py-2.5 rounded-lg text-white flex items-center ${
            buttonHighlight ? 'bg-white/30' : 'bg-white/20'
          } transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg`}
          tabIndex={0}
          aria-label="Continue to dashboard"
        >
          <span className="font-medium">Continue</span>
          <ArrowRight className="ml-2 h-4 w-4" />
        </motion.button>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3.2 }}
          className="mt-3 text-sm text-white/70"
        >
          Press <kbd className="px-2 py-0.5 bg-white/20 rounded text-xs mx-1">Enter</kbd> to continue
        </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default WelcomeOverlay;